
import { withSSRAuth } from '@/lib/withSSRAuth';
import StatCard from "@/components/StatCard";
import WalletActivity, { Transaction } from "@/components/WalletActivity";
import { Wallet, ArrowDown, ArrowUp, Clock } from "lucide-react";
import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";

const WalletPage = () => {
  const [userData, setUserData] = useState<{
    amountEarned: number;
    amountWithdrawn: number;
  } | null>(null);
  const [available, setAvailable] = useState<number | null>(null);
  const [pending, setPending] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stripeAccountLinked, setStripeAccountLinked] = useState<boolean | null>(null);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetch('/api/user').then((res) => res.json()),
      fetch('/api/driver/balance').then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Error fetching balance');
        return data;
      }),
    ])
      .then(([user, balanceData]) => {
        setUserData(user);
        if (balanceData.stripeAccountLinked === false) {
          setStripeAccountLinked(false);
          setAvailable(0);
          setPending(0);
        } else {
          setStripeAccountLinked(true);
          const availGb = balanceData.available.find((b: any) => b.currency === 'gbp');
          const pendGb = balanceData.pending.find((b: any) => b.currency === 'gbp');
          setAvailable(availGb ? availGb.amount / 100 : 0);
          setPending(pendGb ? pendGb.amount / 100 : 0);
        }
      })
      .catch((err: any) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <main className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-10 w-48 mb-6" />
          <Skeleton className="h-4 w-64 mb-10" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {Array.from({ length: 4 }).map((_, idx) => (
              <Skeleton key={idx} className="h-24 w-full" />
            ))}
          </div>
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, idx) => (
              <Skeleton key={idx} className="h-48 w-full" />
            ))}
          </div>
        </div>
      </main>
    );
  }
  if (error) {
    return (
      <main className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
        <div className="max-w-7xl mx-auto">
          <p className="text-red-600">{error}</p>
        </div>
      </main>
    );
  }

  const transactions: Transaction[] = [];

  return (
    <main className="flex-1 p-6 md:p-10 ml-0 md:ml-64">
      <div className="max-w-7xl mx-auto">
        {/* Hero */}
        <header className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight">Wallet</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track earnings in real time and see what’s ready to withdraw.
          </p>
        </header>

        {/* Stripe notice */}
        {stripeAccountLinked === false && (
          <div className="mb-6 rounded-lg border bg-primary/5 p-4">
            <p className="text-sm text-muted-foreground">
              Connect your Stripe account to enable quoting and payouts. You can do this from
              <a href="/settings" className="ml-1 underline text-primary">Settings</a>.
            </p>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Available"
            value={`£${available!.toFixed(2)}`}
            icon={<Wallet size={20} />}
            className="bg-primary/5 border-primary/20"
          />
          <StatCard title="Pending" value={`£${pending!.toFixed(2)}`} icon={<Clock size={20} />} />
          <StatCard title="Total earned" value={`£${userData!.amountEarned.toFixed(2)}`} icon={<ArrowDown size={20} />} />
          <StatCard title="Withdrawn" value={`£${userData!.amountWithdrawn.toFixed(2)}`} icon={<ArrowUp size={20} />} />
        </div>

        {/* History */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <div className="rounded-xl border bg-card">
            <div className="px-6 pt-6">
              <h2 className="text-xl font-semibold tracking-tight">Transaction history</h2>
              <p className="text-sm text-muted-foreground mt-1">
                A detailed ledger of payouts, fees, and job earnings.
              </p>
            </div>
            <div className="p-6">
              <WalletActivity transactions={transactions} />
              {transactions.length === 0 && (
                <p className="text-sm text-muted-foreground text-center">
                  No transactions yet. Complete jobs to see your earnings here.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Help */}
        <div className="rounded-xl border bg-card p-6">
          <h3 className="text-base font-medium">How payouts work</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Balances marked as Available can be paid out to your bank. Pending balances are
            usually cleared within a few days depending on Stripe’s schedule.
          </p>
        </div>
      </div>
    </main>
  );
};

export default withSSRAuth(WalletPage, { role: 'USER', type: 'DRIVER' });
